import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { calculateCissScores } from '../../utils/assessmentScoring';
import CissResults from './CissResults';

interface CissAssessmentProps {
  onBack: () => void;
}

const questions = [
  // Task-oriented coping (1-16)
  { id: 1, text: 'Make an action plan to solve the problem', style: 'task' },
  { id: 2, text: 'Focus on the problem and think about solutions', style: 'task' },
  { id: 3, text: 'Analyze the situation to understand it better', style: 'task' },
  { id: 4, text: 'Adjust my priorities to deal with the situation', style: 'task' },
  { id: 5, text: 'Take direct action to solve the problem', style: 'task' },
  { id: 6, text: 'Set specific goals to address the situation', style: 'task' },
  { id: 7, text: 'Think carefully about the best solution', style: 'task' },
  { id: 8, text: 'Put extra effort into solving the problem', style: 'task' },
  { id: 9, text: 'List different solutions to the problem', style: 'task' },
  { id: 10, text: 'Learn from past experiences', style: 'task' },
  { id: 11, text: 'Stay calm and think objectively', style: 'task' },
  { id: 12, text: 'Systematically solve the problem', style: 'task' },
  { id: 13, text: 'Gather more information to make decisions', style: 'task' },
  { id: 14, text: 'Evaluate pros and cons of different options', style: 'task' },
  { id: 15, text: 'Seek professional advice', style: 'task' },
  { id: 16, text: 'Create specific steps for action', style: 'task' },

  // Emotion-oriented coping (17-32)
  { id: 17, text: 'Blame myself for getting into this situation', style: 'emotion' },
  { id: 18, text: 'Get angry at myself', style: 'emotion' },
  { id: 19, text: 'Worry about not being able to cope', style: 'emotion' },
  { id: 20, text: 'Become very tense', style: 'emotion' },
  { id: 21, text: 'Fantasize about how things might turn out', style: 'emotion' },
  { id: 22, text: 'Feel anxious about what is happening', style: 'emotion' },
  { id: 23, text: 'Recall similar difficult experiences', style: 'emotion' },
  { id: 24, text: 'Tell myself things could be worse', style: 'emotion' },
  { id: 25, text: 'Feel helpless about the situation', style: 'emotion' },
  { id: 26, text: 'Get preoccupied with my worries', style: 'emotion' },
  { id: 27, text: 'Become very emotional', style: 'emotion' },
  { id: 28, text: 'Feel frustrated about my reactions', style: 'emotion' },
  { id: 29, text: 'Wish the situation would go away', style: 'emotion' },
  { id: 30, text: 'Feel dissatisfied with my handling of things', style: 'emotion' },
  { id: 31, text: 'Feel very stressed', style: 'emotion' },
  { id: 32, text: 'Have difficulty controlling my emotions', style: 'emotion' },

  // Avoidance-oriented coping (33-48)
  { id: 33, text: 'Try to temporarily forget about the whole thing', style: 'avoidance' },
  { id: 34, text: 'Spend time with friends', style: 'avoidance' },
  { id: 35, text: 'Watch TV or play games to distract myself', style: 'avoidance' },
  { id: 36, text: 'Do other things to avoid thinking about it', style: 'avoidance' },
  { id: 37, text: 'Talk to others to avoid the problem', style: 'avoidance' },
  { id: 38, text: 'Go shopping or engage in retail therapy', style: 'avoidance' },
  { id: 39, text: 'Try to sleep to escape the problem', style: 'avoidance' },
  { id: 40, text: 'Go for a walk or exercise', style: 'avoidance' },
  { id: 41, text: 'Visit family or friends', style: 'avoidance' },
  { id: 42, text: 'Do activities I enjoy', style: 'avoidance' },
  { id: 43, text: 'Eat to make myself feel better', style: 'avoidance' },
  { id: 44, text: 'Spend time alone', style: 'avoidance' },
  { id: 45, text: 'Do something relaxing', style: 'avoidance' },
  { id: 46, text: 'Put the problem aside temporarily', style: 'avoidance' },
  { id: 47, text: 'Focus on other tasks', style: 'avoidance' },
  { id: 48, text: 'Seek social support', style: 'avoidance' }
];

const options = [
  { value: 1, label: 'Not at all' },
  { value: 2, label: 'Rarely' },
  { value: 3, label: 'Sometimes' },
  { value: 4, label: 'Often' },
  { value: 5, label: 'Very often' }
];

const CissAssessment: React.FC<CissAssessmentProps> = ({ onBack }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showInstructions, setShowInstructions] = useState(true);

  if (showInstructions) {
    return (
      <div className="flex flex-col h-full">
        <div className="bg-white rounded-xl shadow-lg p-6 flex-1 overflow-y-auto">
          <div className="flex items-center mb-6">
            <button
              onClick={onBack}
              className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">Coping Style Assessment (CISS)</h2>
          </div>
          
          <div className="space-y-4 mb-8">
            <p className="text-gray-600">
              The following are possible behaviors people might engage in when facing stressful or difficult situations. 
              Please indicate how often you typically use each of these behaviors when dealing with stress.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Rating Scale:</h3>
              <ul className="space-y-2">
                {options.map(option => (
                  <li key={option.value}>{option.value} = {option.label}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="p-4 bg-white border-t">
          <button
            onClick={() => setShowInstructions(false)}
            className="w-full py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
          >
            Start Assessment
          </button>
        </div>
      </div>
    );
  }

  if (currentQuestion >= questions.length) {
    const scores = calculateCissScores(answers);
    return <CissResults scores={scores} onBack={onBack} />;
  }

  const handleAnswer = (score: number) => {
    setAnswers(prev => ({
      ...prev,
      [questions[currentQuestion].id]: score
    }));

    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      setCurrentQuestion(questions.length);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Question {currentQuestion + 1} / {questions.length}</h2>
              <span className="text-sm text-gray-500">
                Progress: {Math.round((currentQuestion / questions.length) * 100)}%
              </span>
            </div>
            
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-indigo-500 transition-all duration-300"
                style={{ width: `${(currentQuestion / questions.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="mb-6">
            <p className="text-lg text-gray-800 mb-6">{questions[currentQuestion].text}</p>
            
            <div className="grid grid-cols-1 gap-3">
              {options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(option.value)}
                  className={`
                    p-4 rounded-lg border-2 transition-all flex items-center justify-between
                    ${answers[questions[currentQuestion].id] === option.value
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-indigo-200 hover:bg-gray-50'
                    }
                  `}
                >
                  <span className="text-gray-800">{option.label}</span>
                  <span className="font-semibold text-indigo-600">{option.value}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 bg-white border-t mt-auto">
        <div className="flex justify-between items-center">
          <button
            onClick={() => setCurrentQuestion(prev => Math.max(0, prev - 1))}
            disabled={currentQuestion === 0}
            className={`
              px-4 py-2 rounded-lg
              ${currentQuestion === 0
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
              }
            `}
          >
            Previous
          </button>
          
          <div className="text-sm text-gray-500">
            {currentQuestion + 1} / {questions.length}
          </div>

          {currentQuestion === questions.length - 1 && Object.keys(answers).length === questions.length && (
            <button
              onClick={() => setCurrentQuestion(questions.length)}
              className="px-6 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
            >
              View Results
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CissAssessment;